import { useEffect, useRef, useState } from "react";
import { Modal } from "bootstrap";
import { ICustomerProfile } from "../models/ICustomerProfile";
import { IUserVerificationStatus } from "../models/IUserVerificationStatus";
import { getCustomerProfile, updateUserProfile } from "../services/userService";
import { getUserVerificationStatus, resendPhoneOtp, resendEmailOtp } from "../services/auth";
import { getUserToken } from "../utils/tokenUtils";
import { getApiErrorMessage } from "../utils/Util";
import { useLanguage } from "../context/LanguageContext";
import PhoneVerificationPopup from "../popupPages/PhoneVerificationPopup";
import OtpVerificationPopup from "../popupPages/OtpVerificationPopup";
import EmailVerificationPopup from "../popupPages/EmailVerificationPopup";
import EmailPreferencesPopup from "../popupPages/EmailPreferencesPopup";
import BirthDatePicker from "../components/BirthDatePicker";
import { MIN_USER_AGE } from "../legal/legalContent";

function Profil() {
  const [profile, setProfile] = useState<ICustomerProfile | null>(null);
  const [verification, setVerification] = useState<IUserVerificationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [addingBirth, setAddingBirth] = useState(false);
  const [birthInput, setBirthInput] = useState("");
  const [birthSaving, setBirthSaving] = useState(false);
  const [birthError, setBirthError] = useState("");

  const [phoneVerifId, setPhoneVerifId] = useState("");
  const [dogrulaLoading, setDogrulaLoading] = useState(false);
  const [dogrulaError, setDogrulaError] = useState("");
  const [otpSentAt, setOtpSentAt] = useState(0);

  const otpOpenRef = useRef<HTMLButtonElement>(null);
  const [emailVerifId, setEmailVerifId] = useState("");
  const [emailDogrulaLoading, setEmailDogrulaLoading] = useState(false);
  const [emailDogrulaError, setEmailDogrulaError] = useState("");

  const [editingEmail, setEditingEmail] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSaveError, setEmailSaveError] = useState("");

  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneSaveError, setPhoneSaveError] = useState("");


  const { t } = useLanguage();

  const token = getUserToken();

  const loadProfile = () => {
    if (!token) return;
    getCustomerProfile().then((res) => setProfile(res.data));
  };

  const loadVerification = () => {
    if (!token) return;
    getUserVerificationStatus(token.userId)
      .then((res) => setVerification(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    if (!token) {
      window.location.href = "/?showLogin=1";
      return;
    }
    Promise.all([
      getCustomerProfile(),
      getUserVerificationStatus(token.userId),
    ])
      .then(([profileRes, verRes]) => {
        setProfile(profileRes.data);
        setVerification(verRes.data);
      })
      .catch(() => setError(t("profile.loadError")))
      .finally(() => setLoading(false));

  }, []);

  const isValidBirthDate = (isoDate: string | null | undefined) => {
    if (!isoDate) return false;
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return false;
    // .NET default DateTime (0001-01-01) → geçersiz say
    if (d.getFullYear() <= 1) return false;
    return true;
  };

  const formatBirthDate = (isoDate: string) => {
    if (!isValidBirthDate(isoDate)) return "";
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const handleDogrulaClick = async () => {
    const phone = profile?.customerTelNo;
    if (!phone) return;
    setDogrulaLoading(true);
    setDogrulaError("");
    try {
      const res = await resendPhoneOtp(phone, profile?.verification?.id ?? "");
      setPhoneVerifId(res.data.verificationId);
      setOtpSentAt(Date.now());
      otpOpenRef.current?.click();
    } catch (err) {
      setDogrulaError(getApiErrorMessage(err, t("profile.otpFailed")));
    } finally {
      setDogrulaLoading(false);
    }
  };

  const handleEmailDogrulaClick = async () => {
    const email = profile?.customerEmail;
    if (!email) return;
    setEmailDogrulaLoading(true);
    setEmailDogrulaError("");
    try {
      const res = await resendEmailOtp(email);
      setEmailVerifId(res.data.verificationId);
      setTimeout(() => {
        const el = document.getElementById("profileEmailVerificationModal");
        if (el) Modal.getOrCreateInstance(el).show();
      }, 50);
    } catch (err) {
      setEmailDogrulaError(getApiErrorMessage(err, t("profile.otpFailed")));
    } finally {
      setEmailDogrulaLoading(false);
    }
  };

  const handleSaveBirth = async () => {
    if (!birthInput || !token) return;
    setBirthSaving(true);
    setBirthError("");
    try {
      await updateUserProfile(token.userId, { customerBirthDate: new Date(birthInput).toISOString() });
      window.location.reload();
    } catch (err) {
      setBirthError(getApiErrorMessage(err, t("profile.birthSaveFailed")));
      setBirthSaving(false);
    }
  };


  const handleSavePhone = async () => {
    if (!phoneInput.trim() || !token) return;
    setPhoneSaving(true);
    setPhoneSaveError("");
    try {
      await updateUserProfile(token.userId, { customerTelNo: phoneInput.trim() });
      window.location.reload();
    } catch (err) {
      setPhoneSaveError(getApiErrorMessage(err, t("profile.phoneSaveFailed")));
      setPhoneSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!emailInput.trim() || !token) return;
    setEmailSaving(true);
    setEmailSaveError("");
    try {
      await updateUserProfile(token.userId, { customerEmail: emailInput.trim() });
      window.location.reload();
    } catch (err) {
      setEmailSaveError(getApiErrorMessage(err, t("profile.emailSaveFailed")));
      setEmailSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-4" style={{ maxWidth: "480px" }}>
        <div className="skeleton mb-4" style={{ height: "24px", width: "40%" }} />
        <div className="card border-0 shadow-sm rounded-4 p-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="mb-3">
              <div className="skeleton mb-2" style={{ height: "13px", width: "30%" }} />
              <div className="skeleton" style={{ height: "38px", borderRadius: "8px" }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  const hasPhone = !!profile?.customerTelNo;
  const hasBirth = isValidBirthDate(profile?.customerBirthDate);
  const phoneVerified = verification?.phoneVerified ?? true;
  const emailVerified = verification?.emailVerified ?? true;
  const profileIncomplete = !emailVerified || !hasPhone || !phoneVerified || !hasBirth;

  return (
    <div className="container py-4" style={{ maxWidth: "480px" }}>
      <h5 className="fw-bold mb-4">
        <i className="bi bi-person-circle me-2 text-danger"></i>
        {t("profile.title")}
      </h5>

      {/* Tamamlanmış bilgiler */}
      <div className="card border-0 shadow-sm rounded-4 p-4 mb-3">
        <div className="mb-3">
          <label className="form-label small fw-semibold text-muted mb-1">{t("profile.firstName")}</label>
          <input type="text" className="form-control" value={profile?.customerName ?? ""} readOnly />
        </div>
        <div className="mb-3">
          <label className="form-label small fw-semibold text-muted mb-1">{t("profile.lastName")}</label>
          <input type="text" className="form-control" value={profile?.customerSurName ?? ""} readOnly />
        </div>
        {emailVerified && (
          <div className="mb-3">
            <label className="form-label small fw-semibold text-muted mb-1">{t("profile.email")}</label>
            {editingEmail ? (
              <>
                <div className="input-group">
                  <input
                    type="email"
                    className="form-control"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder={profile?.customerEmail ?? ""}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleSaveEmail}
                    disabled={!emailInput.trim() || emailSaving}
                  >
                    {emailSaving ? <span className="spinner-border spinner-border-sm" /> : t("profile.changeEmail")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => { setEditingEmail(false); setEmailInput(""); setEmailSaveError(""); }}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
                {emailSaveError && (
                  <div className="mt-1 small text-danger">
                    <i className="bi bi-x-circle-fill me-1"></i>{emailSaveError}
                  </div>
                )}
              </>
            ) : (
              <div className="input-group">
                <input type="email" className="form-control" value={profile?.customerEmail ?? ""} readOnly />
                <span className="input-group-text text-success border-success">
                  <i className="bi bi-check-circle-fill"></i>
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => { setEditingEmail(true); setEmailInput(profile?.customerEmail ?? ""); }}
                >
                  <i className="bi bi-pencil"></i>
                </button>
              </div>
            )}
          </div>
        )}
        {hasPhone && phoneVerified && (
          <div className="mb-3">
            <label className="form-label small fw-semibold text-muted mb-1">{t("profile.phone")}</label>
            {editingPhone ? (
              <>
                <div className="input-group">
                  <input
                    type="tel"
                    className="form-control"
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                    placeholder={profile!.customerTelNo}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleSavePhone}
                    disabled={!phoneInput.trim() || phoneSaving}
                  >
                    {phoneSaving ? <span className="spinner-border spinner-border-sm" /> : t("profile.changePhone")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => { setEditingPhone(false); setPhoneInput(""); setPhoneSaveError(""); }}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
                {phoneSaveError && (
                  <div className="mt-1 small text-danger">
                    <i className="bi bi-x-circle-fill me-1"></i>{phoneSaveError}
                  </div>
                )}
              </>
            ) : (
              <div className="input-group">
                <input type="text" className="form-control" value={profile!.customerTelNo} readOnly />
                <span className="input-group-text text-success border-success">
                  <i className="bi bi-check-circle-fill"></i>
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => { setEditingPhone(true); setPhoneInput(profile!.customerTelNo); }}
                >
                  <i className="bi bi-pencil"></i>
                </button>
              </div>
            )}
          </div>
        )}
        {hasBirth && (
          <div className="mb-0">
            <label className="form-label small fw-semibold text-muted mb-1">{t("profile.birthDate")}</label>
            <div className="input-group">
              <input type="text" className="form-control" value={formatBirthDate(profile!.customerBirthDate)} readOnly />
              <span className="input-group-text text-success border-success">
                <i className="bi bi-check-circle-fill"></i>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Eksik / doğrulanmamış bilgiler */}
      {profileIncomplete && (
        <div className="card shadow-sm rounded-4 p-4 mb-3" style={{ borderColor: "#c20e1a", borderWidth: "2px", borderStyle: "solid" }}>
          <div className="d-flex align-items-center gap-2 mb-3">
            <i className="bi bi-exclamation-triangle-fill text-danger fs-5"></i>
            <span className="fw-semibold text-danger small">{t("profile.completeProfile")}</span>
          </div>

          {/* E-posta doğrulama */}
          {!emailVerified && (
            <div className="mb-3">
              <label className="form-label small fw-semibold text-muted mb-1">{t("profile.email")}</label>
              <div className="input-group">
                <input type="email" className="form-control" value={profile?.customerEmail ?? ""} readOnly />
                <button
                  type="button"
                  className="btn btn-outline-warning"
                  onClick={handleEmailDogrulaClick}
                  disabled={emailDogrulaLoading}
                >
                  {emailDogrulaLoading
                    ? <span className="spinner-border spinner-border-sm me-1" />
                    : <i className="bi bi-envelope-check me-1"></i>
                  }
                  {t("common.verify")}
                </button>
              </div>
              {emailDogrulaError && (
                <div className="mt-1 small text-danger">
                  <i className="bi bi-x-circle-fill me-1"></i>
                  {emailDogrulaError}
                </div>
              )}
            </div>
          )}

          {/* Telefon eksik veya doğrulanmamış */}
          {(!hasPhone || !phoneVerified) && (
            <div className="mb-3">
              <label className="form-label small fw-semibold text-muted mb-1">{t("profile.phone")}</label>
              {hasPhone ? (
                <>
                  <div className="input-group">
                    <input type="text" className="form-control" value={profile!.customerTelNo} readOnly />
                    <button
                      type="button"
                      className="btn btn-outline-warning"
                      onClick={handleDogrulaClick}
                      disabled={dogrulaLoading}
                    >
                      {dogrulaLoading
                        ? <span className="spinner-border spinner-border-sm me-1" />
                        : <i className="bi bi-shield-check me-1"></i>
                      }
                      {t("common.verify")}
                    </button>
                  </div>
                  {dogrulaError && (
                    <div className="mt-1 small text-danger">
                      <i className="bi bi-x-circle-fill me-1"></i>
                      {dogrulaError}
                    </div>
                  )}
                </>
              ) : (
                <div className="input-group">
                  <input type="text" className="form-control" value="" placeholder={t("profile.noPhone")} readOnly />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    data-bs-toggle="modal"
                    data-bs-target="#phoneVerificationModal"
                  >
                    <i className="bi bi-plus-lg me-1"></i>{t("common.add")}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Doğum tarihi eksik */}
          {!hasBirth && (
            <div className="mb-0">
              <label className="form-label small fw-semibold text-muted mb-1">{t("profile.birthDate")}</label>
              {addingBirth ? (
                <div className="input-group">
                  <BirthDatePicker
                    value={birthInput}
                    maxDate={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - MIN_USER_AGE); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })()}
                    onChange={setBirthInput}
                    autoOpen
                  />
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleSaveBirth}
                    disabled={!birthInput || birthSaving}
                  >
                    {t("common.save")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => { setAddingBirth(false); setBirthInput(""); setBirthError(""); }}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              ) : (
                <div className="input-group">
                  <input type="text" className="form-control" value="" placeholder={t("profile.noBirthDate")} readOnly />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => setAddingBirth(true)}
                  >
                    <i className="bi bi-plus-lg me-1"></i>{t("common.add")}
                  </button>
                </div>
              )}
              {birthError && (
                <div className="mt-1 small text-danger">
                  <i className="bi bi-x-circle-fill me-1"></i>
                  {birthError}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* E-posta Tercihleri */}
      <button
        type="button"
        className="btn btn-outline-danger w-100 rounded-pill py-2 mb-3 d-flex align-items-center justify-content-center gap-2"
        data-bs-toggle="modal"
        data-bs-target="#emailPreferencesModal"
      >
        <i className="bi bi-envelope-at-fill"></i>
        {t("emailPreferences.title")}
        <i className="bi bi-chevron-right ms-auto"></i>
      </button>

      <button
        ref={otpOpenRef}
        className="d-none"
        data-bs-toggle="modal"
        data-bs-target="#profileOtpModal"
      />

      <PhoneVerificationPopup
        userId={token?.userId ?? ""}
        onSuccess={() => window.location.reload()}
      />

      <OtpVerificationPopup
        modalId="profileOtpModal"
        registerData={null}
        phoneNumber={profile?.customerTelNo ?? ""}
        verificationId={phoneVerifId}
        sentAt={otpSentAt}
        onSuccess={() => window.location.reload()}
      />

      <EmailVerificationPopup
        modalId="profileEmailVerificationModal"
        verificationId={emailVerifId}
        onSuccess={() => window.location.reload()}
      />

      <EmailPreferencesPopup />
    </div>
  );
}

export default Profil;
